import React, { useState, useRef } from 'react';
import './PerguntasApoio.css';

const PerguntasApoio = props => (
    <Questionnaire key={JSON.stringify(props.perguntasApoio ?? null)} {...props} />
);

const Questionnaire = ({
    perguntasApoio, 
    onComplete, 
    client_id, 
    marca, 
    origem, 
    messages, 
    setMessages, 
    localizacao, 
    operational_system, 
    version_app,
    isCompleted = false 
}) => {
    // Filtrar perguntas que tenham validações (NOMINAL com opções, QUANTITATIVO com min/max ou LATERALIDADE esquerdo, direito e bilateral)
    const perguntasValidas = (perguntasApoio && Array.isArray(perguntasApoio)) ? perguntasApoio.filter(pergunta => {
        if (!pergunta?.validacoes) return false;
        
        // Perguntas NOMINAL com opções válidas
        if (pergunta.tipo === 'NOMINAL' && pergunta.validacoes.opcoesValidas && pergunta.validacoes.opcoesValidas.length > 0) {
            return true;
        }
        
        // Perguntas QUANTITATIVO com validações de min/max
        if (pergunta.tipo === 'QUANTITATIVO' && (pergunta.validacoes.minimo !== undefined || pergunta.validacoes.maximo !== undefined)) {
            return true;
        }

        // Perguntas LATERALIDADE com opções válidas
        if (pergunta.tipo === 'LATERALIDADE' && pergunta.validacoes.opcoesValidas && pergunta.validacoes.opcoesValidas.length > 0) {
            return true;
        }
        
        return false;
    }).map(pergunta => ({
        ...pergunta,
        // Legacy payloads use id; answers still use the identificador contract.
        identificador: pergunta.identificador ?? pergunta.id
    })) : [];

    const identifiers = perguntasValidas.map(pergunta => pergunta.identificador);
    const invalidIdentifiers = identifiers.some(identifier =>
        !((typeof identifier === 'string' && identifier.trim() !== '') ||
          (typeof identifier === 'number' && Number.isFinite(identifier)))
    ) || new Set(identifiers.map(String)).size !== identifiers.length ||
        (Array.isArray(perguntasApoio) && perguntasApoio.some(pergunta => !pergunta));
    
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [respostas, setRespostas] = useState({});
    const [inputValues, setInputValues] = useState({});
    const submitted = useRef(false);

    // Função para validar resposta quantitativa
    const validarRespostaQuantitativa = (pergunta, valor) => {
        if (!valor || valor.trim() === '') return false;
        
        const numero = parseFloat(valor);
        if (isNaN(numero)) return false;
        
        const { minimo, maximo, incluiMinimo, incluiMaximo } = pergunta.validacoes;
        
        if (minimo !== undefined) {
            const min = parseFloat(minimo);
            if (incluiMinimo ? numero < min : numero <= min) {
                return false;
            }
        }
        
        if (maximo !== undefined) {
            const max = parseFloat(maximo);
            if (incluiMaximo ? numero > max : numero >= max) {
                return false;
            }
        }
        
        return true;
    };

    // Função para responder uma pergunta
    const handleResposta = (identificador, resposta) => {
        if (submitted.current || isCompleted) return;
        const novasRespostas = {
            ...respostas,
            [identificador]: resposta
        };
        setRespostas(novasRespostas);

        // Se não é a última pergunta, vai para a próxima
        if (currentQuestionIndex < perguntasValidas.length - 1) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        } else {
            // É a última pergunta, finalizar
            finalizarPerguntas(novasRespostas);
        }
    };

    // Função para lidar com mudanças no input quantitativo
    const handleInputChange = (identificador, valor) => {
        setInputValues({
            ...inputValues,
            [identificador]: valor
        });
    };

    // Função para confirmar resposta quantitativa
    const confirmarRespostaQuantitativa = (pergunta) => {
        const valor = inputValues[pergunta.identificador] || '';
        if (validarRespostaQuantitativa(pergunta, valor)) {
            handleResposta(pergunta.identificador, valor);
        }
    };

    // Função para navegar para pergunta anterior
    const handleAnterior = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex(currentQuestionIndex - 1);
        }
    };

    // Função para navegar para próxima pergunta
    const handleProxima = () => {
        // Só permite avançar se a pergunta atual foi respondida
        if (currentQuestionIndex < perguntasValidas.length - 1 && perguntaAtualRespondida) {
            setCurrentQuestionIndex(currentQuestionIndex + 1);
        }
    };

    // Função para finalizar as perguntas
    const finalizarPerguntas = (respostasFinais) => {
        if (submitted.current || isCompleted || !perguntasValidas.every(pergunta =>
            Object.prototype.hasOwnProperty.call(respostasFinais, pergunta.identificador)
        )) return;
        submitted.current = true;
        // Formatar respostas para envio
        const respostasFormatadas = Object.entries(respostasFinais).map(([identificador, response]) => ({
            identificador,
            response
        }));

        // Chamar função de callback para enviar para a API
        if (onComplete) {
            onComplete(respostasFormatadas);
        }
    };

    // Verificar se a pergunta atual foi respondida
    const perguntaAtualRespondida = perguntasValidas[currentQuestionIndex] && 
        Object.prototype.hasOwnProperty.call(respostas, perguntasValidas[currentQuestionIndex].identificador);

    // Verificar se pergunta quantitativa atual está válida para confirmação
    const perguntaQuantitativaValida = () => {
        const perguntaAtual = perguntasValidas[currentQuestionIndex];
        if (!perguntaAtual || perguntaAtual.tipo !== 'QUANTITATIVO') return false;
        const valor = inputValues[perguntaAtual.identificador] || '';
        return validarRespostaQuantitativa(perguntaAtual, valor);
    };

    // Função para navegar pelos pontos indicadores
    const navegarParaPergunta = (index) => {
        // Só permite navegar para:
        // 1. Perguntas anteriores (já respondidas)
        // 2. A próxima pergunta se a atual foi respondida
        if (index <= currentQuestionIndex || 
            (index === currentQuestionIndex + 1 && perguntaAtualRespondida)) {
            setCurrentQuestionIndex(index);
        }
    };

    if (isCompleted) {
        return (
            <div className="perguntas-apoio-container">
                <div className="perguntas-apoio-card completed-simple">
                    <div className="pergunta-content">
                        <p>Obrigado! As respostas foram registradas.</p>
                    </div>
                </div>
            </div>
        );
    }

    if (invalidIdentifiers) {
        return <div role="alert">Não foi possível carregar as perguntas. Tente novamente.</div>;
    }

    if (!perguntasValidas || perguntasValidas.length === 0) {
        return null;
    }

    const perguntaAtual = perguntasValidas[currentQuestionIndex];

    return (
        <div className="perguntas-apoio-container">
            <div className="perguntas-apoio-card">
                <div className="pergunta-header">
                    <div className="pergunta-contador">
                        Pergunta {currentQuestionIndex + 1} de {perguntasValidas.length}
                    </div>
                    <div className="progresso-bar">
                        <div 
                            className="progresso-fill" 
                            style={{ width: `${((currentQuestionIndex + 1) / perguntasValidas.length) * 100}%` }}
                        ></div>
                    </div>
                </div>

                <div className="pergunta-content">
                    <h3 className="pergunta-titulo">{perguntaAtual.pergunta}</h3>
                    
                    {perguntaAtual.tipo === 'NOMINAL' && (
                        <div className="opcoes-container">
                            {perguntaAtual.validacoes?.opcoesValidas?.map((opcao, index) => (
                                <button
                                    key={index}
                                    className={`opcao-button ${
                                        respostas[perguntaAtual.identificador] === opcao ? 'selected' : ''
                                    }`}
                                    onClick={() => handleResposta(perguntaAtual.identificador, opcao)}
                                >
                                    {opcao}
                                </button>
                            ))}
                        </div>
                    )}

                    {perguntaAtual.tipo === 'LATERALIDADE' && (
                        <div className="opcoes-lateralidade-container">
                            {perguntaAtual.validacoes?.opcoesValidas?.map((opcao, index) => (
                                <div key={index} className="radio-option">
                                    <input
                                        type="radio"
                                        id={`${perguntaAtual.identificador}_${index}`}
                                        name={perguntaAtual.identificador}
                                        value={opcao}
                                        checked={respostas[perguntaAtual.identificador] === opcao}
                                        onChange={() => handleResposta(perguntaAtual.identificador, opcao)}
                                    />
                                    <label htmlFor={`${perguntaAtual.identificador}_${index}`}>
                                        {opcao}
                                    </label>
                                </div>
                            ))}
                        </div>
                    )}

                    {perguntaAtual.tipo === 'QUANTITATIVO' && (
                        <div className="input-quantitativo-container">
                            <div className="input-group">
                                <input
                                    type="number"
                                    className={`input-quantitativo ${
                                        perguntaQuantitativaValida() ? 'valid' : ''
                                    } ${
                                        inputValues[perguntaAtual.identificador] && !perguntaQuantitativaValida() ? 'invalid' : ''
                                    }`}
                                    placeholder={perguntaAtual.pergunta}
                                    value={inputValues[perguntaAtual.identificador] || ''}
                                    onChange={(e) => handleInputChange(perguntaAtual.identificador, e.target.value)}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && perguntaQuantitativaValida()) {
                                            confirmarRespostaQuantitativa(perguntaAtual);
                                        }
                                    }}
                                    step="0.1"
                                    min={perguntaAtual.validacoes?.minimo}
                                    max={perguntaAtual.validacoes?.maximo}
                                />
                                {perguntaAtual.validacoes?.unidadeMedida && (
                                    <span className="unidade-medida">
                                        {perguntaAtual.validacoes.unidadeMedida}
                                    </span>
                                )}
                            </div>
                            
                            <div className="validacao-info">
                                {perguntaAtual.validacoes?.minimo !== undefined && perguntaAtual.validacoes?.maximo !== undefined && (
                                    <span className="range-info">
                                        Entre {perguntaAtual.validacoes.minimo} e {perguntaAtual.validacoes.maximo} {perguntaAtual.validacoes.unidadeMedida || ''}
                                    </span>
                                )}
                                {perguntaAtual.validacoes?.minimo !== undefined && perguntaAtual.validacoes?.maximo === undefined && (
                                    <span className="range-info">
                                        Mínimo: {perguntaAtual.validacoes.minimo} {perguntaAtual.validacoes.unidadeMedida || ''}
                                    </span>
                                )}
                                {perguntaAtual.validacoes?.maximo !== undefined && perguntaAtual.validacoes?.minimo === undefined && (
                                    <span className="range-info">
                                        Máximo: {perguntaAtual.validacoes.maximo} {perguntaAtual.validacoes.unidadeMedida || ''}
                                    </span>
                                )}
                            </div>

                            {perguntaQuantitativaValida() && !perguntaAtualRespondida && (
                                <button 
                                    className="confirmar-button"
                                    onClick={() => confirmarRespostaQuantitativa(perguntaAtual)}
                                >
                                    Confirmar
                                </button>
                            )}

                            {perguntaAtualRespondida && (
                                <div className="resposta-confirmada">
                                    ✓ Resposta registrada: {respostas[perguntaAtual.identificador]} {perguntaAtual.validacoes?.unidadeMedida || ''}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="navegacao-container">
                    <button 
                        className="nav-button anterior" 
                        onClick={handleAnterior}
                        disabled={currentQuestionIndex === 0}
                    >
                        ← Anterior
                    </button>

                    <div className="indicador-paginas">
                        {perguntasValidas.map((_, index) => (
                            <div
                                key={index}
                                className={`indicador-ponto ${
                                    index === currentQuestionIndex ? 'ativo' : ''
                                } ${
                                    Object.prototype.hasOwnProperty.call(respostas, perguntasValidas[index].identificador) ? 'respondido' : ''
                                } ${
                                    index > currentQuestionIndex && !perguntaAtualRespondida ? 'disabled' : ''
                                }`}
                                onClick={() => navegarParaPergunta(index)}
                                style={{
                                    cursor: (index <= currentQuestionIndex || 
                                            (index === currentQuestionIndex + 1 && perguntaAtualRespondida)) 
                                            ? 'pointer' : 'not-allowed',
                                    opacity: (index <= currentQuestionIndex || 
                                             (index === currentQuestionIndex + 1 && perguntaAtualRespondida)) 
                                             ? 1 : 0.5
                                }}
                            ></div>
                        ))}
                    </div>

                    <button 
                        className="nav-button proxima" 
                        onClick={handleProxima}
                        disabled={currentQuestionIndex === perguntasValidas.length - 1 || !perguntaAtualRespondida}
                    >
                        Próxima →
                    </button>
                </div>

                {perguntaAtualRespondida && currentQuestionIndex === perguntasValidas.length - 1 && (
                    <div className="finalizar-container">
                        <button 
                            className="finalizar-button"
                            onClick={() => finalizarPerguntas(respostas)}
                        >
                            Finalizar Perguntas
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PerguntasApoio; 